import { IColumnExprPart } from 'core/models/report-expressions';
import { DateBinningFormatters } from './formatters';

/**
 * The set of first-level types of expressions. Each type may have second-level
 * child types as `ExpressionSubTypes`.
 */
export enum ExpressionTypes {
  Aggregation = 'AggExpr',
  Arithmetic = 'ArithmeticExpr',
  Column = 'ColumnExpr',
  Constant = 'ConstantExpr',
  Keyword = 'KeywordExpr',
  Comparison = 'ComparisonExpr',
  Logical = 'LogicalExpr',
  Date ='DateExpr',
  StringFn = 'StringFnExpr',
  JSONFn = 'JSONFnExpr',
  ArrayFn = 'ArrayFnExpr',
  Config = 'ConfigExpr',
  Condition = 'ConditionExpr',
  WindowFn = 'WindowFnExpr',
  // Placeholder node naming a custom filter variable label; the RHS of a ComparisonExpr.
  // Resolved at fetch time on the backend against the matching CustomFilterDefinition.
  CustomFilterVariable = 'CustomFilterVariableExpr',
  CastFn = 'CastFnExpr',
}

/**
 * The super-set of second-level types of expressions. This set is type-guarded
 * by T which is a first-level type, so that the final type contains only the
 * child types of that first-level type.
 *
 * For instance, `ExpressionSubTypes<AggExpr>` will contain all of `AggExprTypes`
 * (like `Sum`) but none of the others (like `LessThan`).
 */
export type ExpressionSubTypes<T extends ExpressionTypes> =
  T extends ExpressionTypes.Aggregation ? AggExprTypes
  : T extends ExpressionTypes.Arithmetic ? ArithmeticExprTypes
  : T extends ExpressionTypes.Comparison ? CompExprTypes
  : T extends ExpressionTypes.Logical ? LogicalExprTypes
  : T extends ExpressionTypes.Column ? IColumnExprPart
  : T extends ExpressionTypes.Constant ? null
  : T extends ExpressionTypes.CustomFilterVariable ? null
  : T extends ExpressionTypes.Keyword ? KeywordExprTypes
  : T extends ExpressionTypes.Config ? null
  : T extends ExpressionTypes.Date ? DateExprTypes
  : T extends ExpressionTypes.StringFn ? StringFnExprTypes
  : T extends ExpressionTypes.JSONFn ? JSONFnExprTypes
  : T extends ExpressionTypes.ArrayFn ? ArrayFnExprTypes
  : T extends ExpressionTypes.Condition? ConditionExprTypes
  : T extends ExpressionTypes.WindowFn ? WindowFnExprTypes
  : T extends ExpressionTypes.CastFn ? CastFnExprTypes
  : never;

export enum KeywordExprTypes {
  Null = 'NullExpr',
}

export enum AggExprTypes {
  Sum = 'SumExpr',
  SumIf = 'SumIfExpr',
  Max = 'MaxExpr',
  MaxIf = 'MaxIfExpr',
  Min = 'MinExpr',
  MinIf = 'MinIfExpr',
  Avg = 'AvgExpr',
  AvgIf = 'AvgIfExpr',
  Count = 'CountExpr',
  CountIf = 'CountIfExpr',
  CountDistinct = 'CountDistinctExpr',
  CountDistinctIf = 'CountDistinctIfExpr',
  ArgMax = 'ArgMaxExpr',
  ArgMin = 'ArgMinExpr',
  ArgMaxIf = 'ArgMaxIfExpr',
  ArgMinIf = 'ArgMinIfExpr',
  Any = 'AnyExpr',
  AnyHeavy = 'AnyHeavyExpr',
  AnyLast = 'AnyLastExpr',
  GroupConcat = 'GroupConcatExpr',
  GroupConcatDistinct = 'GroupConcatDistinctExpr',
}

export enum WindowFnExprTypes {
  RowNumber = 'RowNumberFnExpr',
  Rank = 'RankFnExpr',
  FirstValue = 'FirstValueFnExpr',
  LastValue = 'LastValueFnExpr',
  Lead = 'LeadFnExpr',
  Lag = 'LagFnExpr',
}

export enum ConditionExprTypes{
  If = 'IfExpr'
}

export enum ArithmeticExprTypes {
  Plus = 'PlusExpr',
  Minus = 'MinusExpr',
  Multiply = 'MultiplyExpr',
  Divide = 'DivideExpr',
}

export type CompExprTypes = FilterOperatorTypes;

export enum LogicalExprTypes {
  And = 'AndExpr',
  Or = 'OrExpr',
  Not = 'NotExpr',
  IsNull = 'IsNullCheckExpr',
}

export enum DateFnExprTypes {
  CurrentDateTime = 'CurrentDateTimeExpr',
  Add = 'DurationAddExpr',
  Subtract = 'DurationSubtractExpr',
}

export enum DateDiffFnExprTypes {
  DifferenceExpr = 'DifferenceExpr',
}

export enum StringFnExprTypes {
  Position = 'PositionExpr',
  Concat = 'ConcatExpr',
  RegexpExtract = 'RegexpExtractExpr',
  Replace = 'ReplaceExpr',
  ReplaceRegexp = 'ReplaceRegexpExpr',
}

export enum JSONFnExprTypes {
  JSONExtract = 'JSONExtractExpr',
}

export type DateExprTypes = DateBinningFormatters | DateFnExprTypes | DateDiffFnExprTypes;

export enum ArrayFnExprTypes {
  ArrayJoinExpr = 'ArrayJoinExpr',
  ArrayStringConcatExpr = 'ArrayStringConcatExpr',
  ArrayIndexOfExpr = 'ArrayIndexOfExpr',
}

export enum CastFnExprTypes {
  Cast = 'CastExpr',
  AccurateCastOrNull = 'AccurateCastOrNullExpr',
  AccurateCastOrDefault = 'AccurateCastOrDefaultExpr',
}

export enum ConfigExprNamespace {
  UserNamespace = 'User',
  TenantSettingsNamespace = 'TenantSettings',
}

export enum TenantSettingKey {
  InitialLeadStage = 'LeadStages.Initial',
  FinalLeadStage = 'LeadStages.Final',
  ActiveLeadStages = 'LeadStages.Active',
  InactiveLeadStages = 'LeadStages.Inactive',
  NonFinalLeadStages = 'LeadStages.NonFinal',
}

export enum TimeDuration {
  SECOND = 'SECOND',
  MINUTE = 'MINUTE',
  HOUR = 'HOUR',
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  QUARTER = 'QUARTER',
  YEAR = 'YEAR',
}

export enum SortDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum CastTargetTypes {
  Integer = 'Integer',
  Decimal = 'Decimal',
  String = 'String',
  Bool = 'Bool',
  Date = 'Date',
  DateTime = 'DateTime',
}

// Case-insensitive lookup of a Cast target literal to its canonical enum member. Single source
// of truth for target normalisation across the parser type-check and the formatter type-map.
const castTargetByLowerName: Record<string, CastTargetTypes> = Object.fromEntries(
  Object.values(CastTargetTypes).map((t) => [t.toLowerCase(), t]),
);

export const resolveCastTargetType = (targetTypeLiteral: string): CastTargetTypes | null => (
  castTargetByLowerName[targetTypeLiteral?.toLowerCase()] ?? null
);

export enum RawAggrExpressionString {
  Sum = 'Sum(%s)',
  SumPartitionBy = 'Sum(%s, %s)',
  Count = 'Count(%s)',
  CountPartitionBy = 'Count(%s, %s)',
  CountIf = 'CountIf(%s)',
  CountIfPartitionBy = 'CountIf(%s, %s)',
  CountDistinct = 'CountDistinct(%s)',
  CountDistinctPartitionBy = 'CountDistinct(%s, %s)',
  CountDistinctIf = 'CountDistinctIf(%s)',
  CountDistinctIfPartitionBy = 'CountDistinctIf(%s, %s)',
  SumIf = 'SumIf(%s)',
  SumIfPartitionBy = 'SumIf(%s, %s)',
  Max = 'Max(%s)',
  MaxPartitionBy = 'Max(%s, %s)',
  Min = 'Min(%s)',
  MinPartitionBy = 'Min(%s, %s)',
  Avg = 'Avg(%s)',
  AvgPartitionBy = 'Avg(%s, %s)',
  MaxIf = 'MaxIf(%s)',
  MaxIfPartitionBy = 'MaxIf(%s, %s)',
  MinIf = 'MinIf(%s)',
  MinIfPartitionBy = 'MinIf(%s, %s)',
  AvgIf = 'AvgIf(%s)',
  AvgIfPartitionBy = 'AvgIf(%s, %s)',
  ArgMax = 'ArgMax(%s)',
  ArgMin = 'ArgMin(%s)',
  ArgMaxIf = 'ArgMaxIf(%s)',
  ArgMinIf = 'ArgMinIf(%s)',
  Any = 'Any(%s)',
  AnyHeavy = 'AnyHeavy(%s)',
  AnyLast = 'AnyLast(%s)',
  GroupConcat = 'GroupConcat(%s)',
  GroupConcatPartitionBy = 'GroupConcat(%s, %s)',
  GroupConcatDistinct = 'GroupConcatDistinct(%s)',
  GroupConcatDistinctPartitionBy = 'GroupConcatDistinct(%s, %s)',
}

export enum RawWindowFnExpressionString {
  RowNumber = 'RowNumber(%s)',
  Rank = 'Rank(%s)',
  FirstValue = 'FirstValue(%s)',
  LastValue = 'LastValue(%s)',
  Lead = 'Lead(%s)',
  Lag = 'Lag(%s)',
}

export enum RawConditionExpressionString {
 If = 'If(%s)',
}

export enum RawDateExpressionString {
  CurrentDateTime = 'CurrentDateTime',
  DurationAdd ='DurationAdd(%s)',
  DurationSubtract = 'DurationSubtract(%s)',
  ToHour = 'ToHour(%s)',
  ToDayOfWeek = 'ToDayOfWeek(%s)',
  ToDayOfMonth = 'ToDayOfMonth(%s)',
  ToDayOfYear = 'ToDayOfYear(%s)',
  ToDate = 'ToDate(%s)',
  ToWeek = 'ToWeek(%s)',
  ToYearWeek = 'ToYearWeek(%s)',
  ToMonth = 'ToMonth(%s)',
  ToMonthOfYear = 'ToMonthOfYear(%s)',
  ToQuarter = 'ToQuarter(%s)',
  ToQuarterOfYear = 'ToQuarterOfYear(%s)',
  ToYear='ToYear(%s)',
  DateDiff = 'DateDiff(%s)',
  DateUnitDiff = 'DateUnitDiff(%s)',
}

export enum RawStringFnExpressionString {
  Position = 'Position(%s)',
  Concat = 'Concat(%s)',
  ConcatSep = 'ConcatSep(%s)',
  ConcatSepFiltered = 'ConcatSepFiltered(%s)',
  RegexpExtract = 'RegexpExtract(%s)',
  Replace = 'Replace(%s)',
  ReplaceRegexp = 'ReplaceRegexp(%s)',
}

export enum RawJSONFnExpression {
  JSONExtract = 'JSONExtract(%s)',
}

export enum RawArrayFnExpressionString {
  ArrayIndex = 'ArrayIndex(%s)',
}

export enum FilterOperatorTypes {
  EQ = 'EQ',
  NOT_EQ = 'NOT_EQ',
  IN = 'IN',
  NOT_IN = 'NOT_IN',
  LIKE = 'LIKE',
  NOT_LIKE = 'NOT_LIKE',
  BETWEEN = 'BETWEEN',
  LT = 'LT',
  GT = 'GT',
  LTE = 'LTE',
  GTE = 'GTE',
  EXISTS = 'EXISTS',
  NOT_EXISTS = 'NOT_EXISTS',
  HAS = 'HAS',
  HAS_ANY = 'HAS_ANY',
  HAS_NOT_ANY = 'HAS_NOT_ANY',
  ILIKE = 'ILIKE',
  IS_NULL = 'IS_NULL',
  IS_NOT_NULL = 'IS_NOT_NULL',
}

export const FilterOperators = new Map<FilterOperatorTypes, string>([
  [FilterOperatorTypes.EQ, '='],
  [FilterOperatorTypes.NOT_EQ, '!='],
  [FilterOperatorTypes.IN, 'in'],
  [FilterOperatorTypes.NOT_IN, 'notin'],
  [FilterOperatorTypes.LIKE, 'LIKE'],
  [FilterOperatorTypes.NOT_LIKE, 'NOT_LIKE'],
  [FilterOperatorTypes.BETWEEN, 'BETWEEN'],
  [FilterOperatorTypes.LT, '<'],
  [FilterOperatorTypes.GT, '>'],
  [FilterOperatorTypes.LTE, '<='],
  [FilterOperatorTypes.GTE, '>='],
  [FilterOperatorTypes.EXISTS, 'EXISTS'],
  [FilterOperatorTypes.NOT_EXISTS, 'NOT_EXISTS'],
  [FilterOperatorTypes.HAS, 'HAS'],
  [FilterOperatorTypes.HAS_ANY, 'HAS_ANY'],
  [FilterOperatorTypes.HAS_NOT_ANY, 'HAS_NOT_ANY'],
  [FilterOperatorTypes.ILIKE, 'ILIKE'],
]);

export const logicalOperatorMap = new Map<LogicalExprTypes, string>([
  [LogicalExprTypes.And, 'and'],
  [LogicalExprTypes.Or, 'or'],
  [LogicalExprTypes.Not, 'not'],
  [LogicalExprTypes.IsNull, 'isNull'],
]);

export const expSyntaxMetaData = {
  Count: {
    exp: 'Count',
    title: 'Count (Field)',
    desc: 'Counts the number of rows or not-NULL values',
    returnText: 'Number',
    example: 'Count(`Lead.ProspectId`)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#Count',
  },
  CountDistinct: {
    exp: 'CountDistinct',
    title: 'CountDistinct (Field)',
    desc: 'Counts the number of unique rows or not-NULL values',
    returnText: 'Number',
    example: 'CountDistinct(`Lead.ProspectId`)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#CountDistinct',
  },
  Sum: {
    exp: 'Sum',
    title: 'Sum (Field)',
    desc: 'Calculates the sum. Only works for numbers',
    returnText: 'Number',
    example: 'Sum(`Lead.mx_Custom_2`)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#Sum',
  },
  SumIf: {
    exp: 'SumIf',
    title: 'SumIf (Field, Condition)',
    desc: 'Calculates the sum of field, if the condition is true',
    returnText: 'Number',
    example: 'Sumif(`Lead.mx_Custom_2`, `Lead.Stage` = \'Prospect\')',
    link: 'https://help.leadsquared.com/siera-expression-builder/#SumIf',
  },
  CountIf: {
    exp: 'CountIf',
    title: 'CountIf (Condition)',
    desc: 'Counts the number of rows where a specific condition is true for a given field',
    returnText: 'Number',
    example: 'CountIf(Lead.Stage` = \'Prospect\')',
    link: 'https://help.leadsquared.com/siera-expression-builder/#CountIf',
  },
  CountDistinctIf: {
    exp: 'CountDistinctIf',
    title: 'CountDistinctIf (Field, Condition)',
    desc: 'Counts the number of distinct values in a field that satisfy a given condition',
    returnText: 'Number',
    example: 'CountDistinctIf(`Lead.ProspectId`, `Lead.Stage` = \'Prospect\')',
    link: 'https://help.leadsquared.com/siera-expression-builder/#CountDistinctIf',
  },
  Max: {
    exp: 'Max',
    title: 'Max (Field)',
    desc: 'Returns the maximum value of a numeric field across all rows',
    returnText: 'Number, DateTime',
    example: 'Max(`Lead.mx_Custom_2`)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#Max',
  },
  MaxIf: {
    exp: 'MaxIf',
    title: 'MaxIf (Field, Condition)',
    desc: 'Returns the maximum value of a numeric field for rows that satisfy a given condition',
    returnText: 'Number, DateTime',
    example: 'MaxIf(`Lead.mx_Custom_2`, `Lead.Stage` = \'Prospect\')',
    link: 'https://help.leadsquared.com/siera-expression-builder/#MaxIf',
  },
  Min: {
    exp: 'Min',
    title: 'Min (Field)',
    desc: 'Returns the minimum value of a numeric field across all rows',
    returnText: 'Number, DateTime',
    example: 'Min(`Lead.mx_Custom_2`)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#Min',
  },
  MinIf: {
    exp: 'MinIf',
    title: 'MinIf (Field, Condition)',
    desc: 'Returns the minimum value of a numeric field for rows that satisfy a given condition',
    returnText: 'Number, DateTime',
    example: 'MinIf(`Lead.mx_Custom_2`, `Lead.Stage` = \'Prospect\')',
    link: 'https://help.leadsquared.com/siera-expression-builder/#MinIf',
  },
  Avg: {
    exp: 'Avg',
    title: 'Avg (Field)',
    desc: 'Calculates the average (mean) value of a numeric field across all rows',
    returnText: 'Number',
    example: 'Avg(`Lead.mx_Custom_2`)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#Avg',
  },
  AvgIf: {
    exp: 'AvgIf',
    title: 'AvgIf (Field, Condition)',
    desc: 'Calculates the average (mean) value of a numeric field for rows that satisfy a given condition',
    returnText: 'Number',
    example: 'AvgIf(`Lead.mx_Custom_2`, `Lead.Stage` = \'Prospect\')',
    link: 'https://help.leadsquared.com/siera-expression-builder/#AvgIf',
  },
  ArgMax: {
    exp: 'ArgMax',
    title: 'ArgMax (TargetField, SelectorField)',
    desc: 'Returns the value from the target field where the selector field (numeric) has the maximum value',
    returnText: 'String, Number, DateTime',
    example: 'ArgMax(`Lead.Source`, `Lead.Score`)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ArgMax',
  },
  ArgMin: {
    exp: 'ArgMin',
    title: 'ArgMin (TargetField, SelectorField)',
    desc: 'Returns the value from the target field where the selector field (numeric) has the minimum value',
    returnText: 'String, Number, DateTime',
    example: 'ArgMin(`Lead.Source`, `Lead.Score`)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ArgMin',
  },
  ArgMaxIf: {
    exp: 'ArgMaxIf',
    title: 'ArgMaxIf (TargetField, SelectorField, Condition)',
    desc: 'Returns the value of the target field for which the selector field is maximum, considering only rows where the condition holds true.',
    returnText: 'String, Number, DateTime',
    example: 'ArgMaxIf(`Lead.Source`, `Lead.Score`, `Lead.Stage` = \'Prospect\')',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ArgMaxIf',
  },
  ArgMinIf: {
    exp: 'ArgMinIf',
    title: 'ArgMinIf (TargetField, SelectorField, Condition)',
    desc: 'Returns the value of the target field for which the selector field is minimum, considering only rows where the condition holds true.',
    returnText: 'String, Number, DateTime',
    example: 'ArgMinIf(`Lead.Source`, `Lead.Score`, `Lead.Stage` = \'Prospect\')',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ArgMinIf',
  },
  Any: {
    exp: 'Any',
    title: 'Any (Field)',
    desc: 'Returns an arbitrary value from the field within the group, useful when all values are equivalent for your analysis.',
    returnText: 'Value of field',
    example: 'Any(`Lead.City`)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#Any',
  },
  AnyHeavy: {
    exp: 'AnyHeavy',
    title: 'AnyHeavy (Field)',
    desc: 'Returns the most frequent value of the field using heavy hitters algorithm, if any value appears more than 50% of the time.',
    returnText: 'Value of field',
    example: 'AnyHeavy(`Lead.City`)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#AnyHeavy',
  },
  AnyLast: {
    exp: 'AnyLast',
    title: 'AnyLast (Field)',
    desc: 'Returns the last value of the given field based on internal processing order.',
    returnText: 'Value of field',
    example: 'AnyLast(`Lead.City`)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#AnyLast',
  },
  GroupConcat: {
    exp: 'GroupConcat',
    title: 'GroupConcat (Field, [Separator])',
    desc: 'Concatenates values of a field across rows in a group into a single string. Separator defaults to a comma.',
    returnText: 'String',
    example: "GroupConcat(`Lead.EmailAddress`, ', ')",
    link: '',
  },
  GroupConcatDistinct: {
    exp: 'GroupConcatDistinct',
    title: 'GroupConcatDistinct (Field, [Separator])',
    desc: 'Concatenates unique values of a field across rows in a group into a single string. Separator defaults to a comma.',
    returnText: 'String',
    example: "GroupConcatDistinct(`Lead.Source`, ', ')",
    link: '',
  },
  ToHour: {
    exp: 'ToHour',
    title: 'ToHour (DateTime Field)',
    desc: 'Extracts the hour component from a given DateTime',
    returnText: 'String ("HH:00\')',
    example: 'CountIf(ToHour(`Lead.CreatedOn`)=\'17:00\')',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ToHour',
  },
  ToDateOfYear: {
    exp: 'ToDateOfYear',
    title: 'ToDateOfYear (DateTime Field)',
    desc: 'Extracts the date of the year from a given DateTime',
    returnText: 'String (YYYY-MM-DD)',
    example: 'CountIf(ToDateOfYear(`Lead.mx_Custom_2`)=\'2023-02-02\')',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ToDateOfYear',
  },
  ToMonthOfYear: {
    exp: 'ToMonthOfYear',
    title: 'ToMonthOfYear (DateTime Field)',
    desc: 'Extracts the month of the year from a given DateTime',
    returnText: 'String (\'000M-0M-01\')',
    example: 'CountIf(ToMonthOfYear(`Lead.mx_Custom_2`)=\'0002-02-01\')',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ToMonthOfYear',
  },
  ToQuarterOfYear: {
    exp: 'ToQuarterOfYear',
    title: 'ToQuarterOfYear (DateTime Field)',
    desc: 'Extracts the quarter of the year from a given DateTime',
    returnText: 'String (\'QX\')',
    example: 'CountIf(ToQuarterOfYear(`Lead.mx_Custom_2`)= \'Q2\')',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ToQuarterOfYear',
  },
  ToYear: {
    exp: 'ToYear',
    title: 'ToYear (DateTime Field)',
    desc: 'Extracts the year from a given DateTime',
    returnText: 'Number (YYYY)',
    example: 'CountIf(ToYear(`Lead.mx_Custom_2`)= 2023)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ToYear',
  },
  ToYearWeek: {
    exp: 'ToYearWeek',
    title: 'ToYearWeek (DateTime Field)',
    desc: 'Extracts the year and week from a given DateTime',
    returnText: 'Number (WW, 1-53)',
    example: 'CountIf(ToYearWeek(`Lead.mx_Custom_2`)=47)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ToYearWeek',
  },
  ToMonth: {
    exp: 'ToMonth',
    title: 'ToMonth (DateTime Field)',
    desc: 'Extracts the month from a given DateTime',
    returnText: 'String (\'YYYY-MM-01\')',
    example: 'CountIf(ToMonth(`Lead.CreatedOn`) = \'2023-02-01\' )',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ToMonth',
  },
  ToQuarter: {
    exp: 'ToQuarter',
    title: 'ToQuarter (DateTime Field)',
    desc: 'Extracts the quarter from a given DateTime',
    returnText: 'String (\'YYYY QX\')',
    example: 'CountIf(ToQuarter(\'Lead.CreatedOn\') = \'2023 Q2\' )',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ToQuater',
  },
  ToDayOfWeek: {
    exp: 'ToDayOfWeek',
    title: 'ToDayOfWeek (DateTime Field)',
    desc: 'Extracts the day of the week from a given DateTime',
    returnText: 'Number (0-6, where 0 is Sunday)',
    example: 'CountIf(ToDayOfWeek(`Lead.CreatedOn`) = 6 )',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ToDayOfWeek',
  },
  ToDayOfMonth: {
    exp: 'ToDayOfMonth',
    title: 'ToDayOfMonth (DateTime Field)',
    desc: 'Extracts the day of the month (1-31) from a date or datetime field',
    returnText: 'Number',
    example: 'ToDayOfMonth(`Lead.Created On`)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ToDayOfMonth',
  },
  ToDayOfYear: {
    exp: 'ToDayOfYear',
    title: 'ToDayOfYear (DateTime Field)',
    desc: 'Returns the day number within the year (1-366) for a given Date/DateTime',
    returnText: 'Number',
    example: 'ToDayOfYear(`Audit.Event Time`)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ToDayOfYear',
  },
  ToDate: {
    exp: 'ToDate',
    title: 'ToDate (DateTime Field)',
    desc: 'Extracts the date component from a given DateTime',
    returnText: 'String',
    example: 'CountIf(ToDate(`Lead.mx_Custom_2`)=\'2023-10-03\')',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ToDate',
  },
  ToWeek: {
    exp: 'ToWeek',
    title: 'ToWeek (DateTime Field)',
    desc: 'Extracts the week from a given DateTime',
    returnText: 'Number (YYYYWW)',
    example: 'CountIf(ToWeek(`Lead.CreatedOn`) = 202347 )',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ToWeek',
  },
  DurationAdd: {
    exp: 'DurationAdd',
    title: 'DurationAdd (DateTime Field, Interval, Time Unit)',
    desc: 'Adds a duration to a given DateTime',
    returnText: 'DateTime',
    example: 'DurationAdd(`Lead.mx_Custom_2`,4, \'MONTH\')',
    link: 'https://help.leadsquared.com/siera-expression-builder/#DurationAdd',
  },
  DateUnitDiff: {
    exp: 'DateUnitDiff',
    title: 'DateUnitDiff(DateTime Field, DateTime Field, TimeUnit)',
    desc: 'Returns the count of the specified Time Unit boundaries crossed between the start and end dates. DateUnitDiff(<start date>, <end date>,<interval>)',
    returnText: 'Number',
    example: 'CountIf(DateUnitDiff(`Lead.CreatedOn`, `Lead.MOdifiedOn`,\'Month\')=4)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#DateUnitDiff',
  },
  DateDiff: {
    exp: 'Age',
    title: 'DateDiff(DateTime Field, DateTime Field, TimeUnit)',
    desc: 'Returns the time elapsed between the start date and end date in terms of the specified time unit.   DateDiff(<start date>, <end date>, <interval>)',
    returnText: 'Number',
    example: 'CountIf(DateDiff(`Lead.CreatedOn`, `Lead.MOdifiedOn`, \'Month\')=4)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#DateDiff',
  },
  DurationSubtract: {
    exp: 'DurationSubtract',
    title: 'DurationSubtract (DateTime Field, Interval, Time Unit)',
    desc: 'Subtracts a duration from a given DateTime',
    returnText: 'DateTime',
    example: 'DurationSubtract(`Lead.mx_Custom_2`,4, \'MONTH\')',
    link: 'https://help.leadsquared.com/siera-expression-builder/#DurationSubtract',
  },
  NotInOp: {
    exp: 'NotInOp',
    title: 'notin [ list of values ]',
    desc: 'Checks if a value is not present in a list of values',
    returnText: 'Boolean',
    example: 'CountIf(`Lead.ProspectStage` notin [\'INVALID\', \'DISQUALIFIED\'])',
    link: 'https://help.leadsquared.com/siera-expression-builder/#notin',
  },
  Null: {
    exp: 'Null',
    title: 'Null',
    desc: 'Represents a null value',
    returnText: 'Null',
    example: 'CountIf(`Lead.ProspectStage` = Null )',
    link: '',
  },
  InOp: {
    exp: 'InOp',
    title: 'in [ list of values ]',
    desc: 'Checks if a value is present in a list of values',
    returnText: 'Boolean',
    example: 'CountIf(`Lead.ProspectStage` in  [\'INVALID\', \'DISQUALIFIED\'])',
    link: 'https://help.leadsquared.com/siera-expression-builder/#in',
  },
  Date: {
    exp: 'Date',
    title: 'Date (YYYY-MM-DD)',
    desc: 'Creates a date using the provided year, month, and day components',
    returnText: 'Date',
    example: 'CountIf(ToDate(`Lead.mx_Custom_2`)=Date(\'2023-10-03\'))',
    link: '',
  },
  CurrentDateTime: {
    exp: 'CurrentDateTime',
    title: 'CurrentDateTime',
    desc: 'Returns the current date and time',
    returnText: 'DateTime',
    example: 'DurationSubtract(CURRENTDATETIME, 4, MONTH))',
    link: 'https://help.leadsquared.com/siera-expression-builder/#CurrentDateTime',
  },
  OrOp: {
    exp: 'OrOp',
    title: '(Condition1) or (Condition2)',
    desc: 'Performs a logical OR operation on multiple conditions, returning true if at least one condition is true',
    returnText: 'Boolean',
    example: '(`Lead.ProspectStage` = \'Customer\') OR (`Lead.mx_Sub_Stage` IN [\'Converted\',\'Paid\'])',
    link: 'https://help.leadsquared.com/siera-expression-builder/#or',
  },
  AndOp: {
    exp: 'AndOp',
    title: '(Condition1) and (Condition2)',
    desc: 'Performs a logical AND operation on multiple conditions, returning true if all conditions are true',
    returnText: 'Boolean',
    example: '(`Lead.ProspectStage` = \'Customer\') AND (`Lead.Source` IN [\'Referral\',\'Affiliate\'])',
    link: 'https://help.leadsquared.com/siera-expression-builder/#and',
  },
  LikeOp: {
    exp: 'LikeOp',
    title: 'String Field like String Literal',
    desc: 'Checks if the string matches the SQL-like pattern (e.g., %abc%).',
    returnText: 'Boolean',
    example: '`Lead.City` like  \'%delhi%\'',
    link: 'https://help.leadsquared.com/siera-expression-builder/#like',
  },
  ILikeOp: {
    exp: 'ILikeOp',
    title: 'String Field ilike String Literal',
    desc: 'Checks if the string matches the SQL-like pattern (e.g., %abc%) in a case-insensitive manner.',
    returnText: 'Boolean',
    example: '`Lead.City` ilike  \'%DELHI%\'',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ilike',
  },
  If: {
    exp: 'If',
    title: 'If(Condition, ValueIfTrue, ValueIfFalse)',
    desc: 'Returns a string based on a condition. The condition can be nested.',
    returnText: 'String',
    example: 'If (`Lead.ProspectStage`= \'Customer\', \'Hot\', If (`Lead.ProspectStage`= \'Prospect\', \'Medium\', \'Cold\' ) )',
    link: 'https://help.leadsquared.com/siera-expression-builder/#if',
  },
  [TenantSettingKey.FinalLeadStage]: {
    exp: 'LeadStages.Final',
    title: 'LeadStages.Final',
    desc: 'Returns the final lead stage',
    returnText: 'String',
    example: 'CountIf(`Lead.Stage` = Config(\'TenantSettings\', \'LeadStages.Final\'))',
    link: '',
  },
  [TenantSettingKey.ActiveLeadStages]: {
    exp: 'LeadStages.Active',
    title: 'LeadStages.Active',
    desc: 'Returns the active lead stages in a list',
    returnText: 'String Array',
    example: 'CountIf(`Lead.Stage` in Config(\'TenantSettings\',\'LeadStages.Active\'))',
    link: '',
  },
  [TenantSettingKey.InitialLeadStage]: {
    exp: 'LeadStages.Initial',
    title: 'LeadStages.Initial',
    desc: 'Returns the default lead stage',
    returnText: 'String',
    example: 'CountIf(`Lead.Stage` = Config(\'TenantSettings\', \'LeadStages.Initial\'))',
    link: '',
  },
  [TenantSettingKey.InactiveLeadStages]: {
    exp: 'LeadStages.Inactive',
    title: 'LeadStages.Inactive',
    desc: 'Returns the inactive lead stages in a list',
    returnText: 'String Array',
    example: 'CountIf(`Lead.Stage` in Config(\'TenantSettings\',\'LeadStages.Inactive\'))',
    link: '',
  },
  [TenantSettingKey.NonFinalLeadStages]: {
    exp: 'LeadStages.NonFinal',
    title: 'LeadStages.NonFinal',
    desc: 'Returns the non-final lead stages in a list',
    returnText: 'String Array',
    example: 'CountIf(`Lead.Stage` in Config(\'TenantSettings\',\'LeadStages.NonFinal\'))',
    link: '',
  },
  Position: {
    exp: 'Position',
    title: 'Position (StringField, Substring)',
    desc: 'Returns the 1-based position of the substring inside the string. 0 if not found.',
    returnText: 'Number',
    example: 'Position(`Lead.Remarks`, \'urgent\')',
    link: 'https://help.leadsquared.com/siera-expression-builder/#Position',
  },
  Concat: {
    exp: 'Concat',
    title: 'Concat (Field1, Field2, ...)',
    desc: 'Concatenates multiple fields into a single string.',
    returnText: 'String',
    example: 'Concat(`Lead.FirstName`, \' \', `Lead.LastName`)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#Concat',
  },
  ConcatSep: {
    exp: 'ConcatSep',
    title: 'ConcatSep (Separator, Field1, Field2, ...)',
    desc: 'Concatenates multiple fields into a single string with a separator between each.',
    returnText: 'String',
    example: 'ConcatSep(\', \', `Lead.FirstName`, `Lead.LastName`)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ConcatSep',
  },
  ConcatSepFiltered: {
    exp: 'ConcatSepFiltered',
    title: 'ConcatSepFiltered (Separator, Field1, Field2, ...)',
    desc: 'Concatenates multiple fields with a separator, filtering out empty strings.',
    returnText: 'String',
    example: 'ConcatSepFiltered(\', \', `Lead.FirstName`, `Lead.MiddleName`, `Lead.LastName`)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ConcatSepFiltered',
  },
  JSONExtract: {
    exp: 'JSONExtract',
    title: 'JSONExtract (json, key1, key2, ...)',
    desc: 'Extracts a string value from a valid JSON field by one or more keys',
    returnText: 'String',
    example: 'JSONExtract(`Lead.Additional Info`, \'address\', \'city\')',
    link: 'https://help.leadsquared.com/siera-expression-builder/#JSONExtract',
  },
  RegexpExtract: {
    exp: 'RegexpExtract',
    title: 'RegexpExtract (str, pattern[, index])',
    desc: 'Extracts a matched group from a string using a regex pattern. Index defaults to 1 if not provided.',
    returnText: 'String',
    example: 'RegexpExtract(`Lead.Email`, \'([^@]+)@\', 1)',
    link: 'https://help.leadsquared.com/siera-expression-builder/#RegexpExtract',
  },
  Replace: {
    exp: 'Replace',
    title: 'Replace (str, pattern, replacement)',
    desc: 'Replaces all occurrences of a pattern in a string with the replacement string.',
    returnText: 'String',
    example: 'Replace(`Lead.Name`, \'old\', \'new\')',
    link: 'https://help.leadsquared.com/siera-expression-builder/#Replace',
  },
  ReplaceRegexp: {
    exp: 'ReplaceRegexp',
    title: 'ReplaceRegexp (str, pattern, replacement)',
    desc: 'Replaces all occurrences of a regex pattern in a string with the replacement string.',
    returnText: 'String',
    example: "ReplaceRegexp(`Lead.Email`, '([^@]+)@', '[REDACTED]')",
    link: 'https://help.leadsquared.com/siera-expression-builder/#ReplaceRegexp',
  },
  Cast: {
    exp: 'Cast',
    title: 'Cast (Field, TargetType)',
    desc: 'Converts a field to the specified type. Supported types: Integer, Decimal, String, Bool, Date, DateTime.',
    returnText: 'Varies by TargetType',
    example: 'Sum(Cast(`Lead.mx_Revenue`, \'Integer\'))',
    link: '',
  },
  AccurateCastOrNull: {
    exp: 'AccurateCastOrNull',
    title: 'AccurateCastOrNull (Field, TargetType)',
    desc: 'Converts a field to the specified type. Returns NULL for values that cannot be converted. Supported types: Integer, Decimal, String, Bool, Date, DateTime.',
    returnText: 'Nullable of TargetType',
    example: 'AccurateCastOrNull(`Lead.mx_Custom_2`, \'Date\')',
    link: '',
  },
  AccurateCastOrDefault: {
    exp: 'AccurateCastOrDefault',
    title: 'AccurateCastOrDefault (Field, TargetType[, DefaultValue])',
    desc: 'Converts a field to the specified type. Returns DefaultValue for values that cannot be converted. Supported types: Integer, Decimal, String, Bool, Date, DateTime.',
    returnText: 'Varies by TargetType',
    example: 'AccurateCastOrDefault(`Lead.mx_Revenue`, \'Integer\', 0)',
    link: '',
  },
  RowNumber: {
    exp: 'RowNumber',
    title: 'RowNumber (PartitionBy, OrderBy)',
    desc: 'Returns the row number of the current row in the partition.',
    returnText: 'Number',
    example: 'RowNumber(PartitionBy(`Lead.Source`), OrderBy(`Lead.Created On` DESC))',
    link: 'https://help.leadsquared.com/siera-expression-builder/#RowNumber',
  },
  Rank: {
    exp: 'Rank',
    title: 'Rank (PartitionBy, OrderBy)',
    desc: 'Assign a rank to rows within each partition according to the specified order. Ties will receive the same rank and leave gaps (e.g., 1, 1, 3).',
    returnText: 'Number',
    example: 'Rank(PartitionBy(`Lead.Source`), OrderBy(`Lead.Score` DESC, `Lead.Created On` ASC))',
    link: 'https://help.leadsquared.com/siera-expression-builder/#Rank',
  },
  FirstValue: {
    exp: 'FirstValue',
    title: 'FirstValue (Field, PartitionBy, OrderBy)',
    desc: 'Returns the first value of the given field in the partition.',
    returnText: 'String, Number, DateTime, Boolean',
    example: 'FirstValue(`Lead.Name`, PartitionBy(`Lead.Source`), OrderBy(`Lead.Created On` DESC))',
    link: 'https://help.leadsquared.com/siera-expression-builder/#FirstValue',
  },
  LastValue: {
    exp: 'LastValue',
    title: 'LastValue (Field, PartitionBy, OrderBy)',
    desc: 'Returns the last value of the given field in the partition.',
    returnText: 'String, Number, DateTime, Boolean',
    example: 'LastValue(`Lead.Name`, PartitionBy(`Lead.Source`), OrderBy(`Lead.Created On` DESC))',
    link: 'https://help.leadsquared.com/siera-expression-builder/#LastValue',
  },
  Lead: {
    exp: 'Lead',
    title: 'Lead (Field, Offset, PartitionBy, OrderBy)',
    desc: 'Returns the value of the given field with the specified offset in the partition.',
    returnText: 'String, Number, DateTime, Boolean',
    example: 'Lead(`Lead.Name`, 1, PartitionBy(`Lead.Source`), OrderBy(`Lead.Created On` DESC))',
    link: 'https://help.leadsquared.com/siera-expression-builder/#Lead',
  },
  Lag: {
    exp: 'Lag',
    title: 'Lag (Field, Offset, PartitionBy, OrderBy)',
    desc: 'Returns the value of the given field with the specified offset before the current row in the partition.',
    returnText: 'String, Number, DateTime, Boolean',
    example: 'Lag(`Lead.Name`, 1, PartitionBy(`Lead.Source`), OrderBy(`Lead.Created On` DESC))',
    link: 'https://help.leadsquared.com/siera-expression-builder/#Lag',
  },
  PartitionBy: {
    exp: 'PartitionBy',
    title: 'PartitionBy (Field)',
    desc: 'Defines how records are grouped into logical segments when used inside a nested/window-style condition.',
    returnText: 'A grouped set of records based on the specified field. Each group is processed independently inside the function.',
    example: "CountIf(`Lead.Status` = 'Converted', PartitionBy(`Lead.Owner Id`))",
    link: '',
  },
  OrderBy: {
    exp: 'OrderBy',
    title: 'OrderBy (Field, SortDirection)',
    desc: 'Specifies the order in which records should be evaluated inside a nested/window-style condition.',
    returnText: 'A sorted sequence of rows, enabling functions to pick the "first", "last", or correctly ordered values when order matters.',
    example: 'FirstValue(`Lead.Status`, PartitionBy(`Lead.Source`), OrderBy(`Lead.Created On` ASC))',
    link: '',
  },
  ArrayIndex: {
    exp: 'ArrayIndex',
    title: 'ArrayIndex (Array, Element)',
    desc: 'Returns the 1-based index of the element in the array. 0 if not found.',
    returnText: 'Number',
    example: 'ArrayIndex(`Lead.Tags`, \'VIP\')',
    link: 'https://help.leadsquared.com/siera-expression-builder/#ArrayIndex',
  },
};

export interface expMetaData{
  exp: string,
  title: string,
  desc:string,
  returnText: string,
  example: string,
  link: string,
}

export const usersPrefix = 'users';
export const JoinPrefix = '_JOIN_';
